import React, { useState, useEffect, useCallback } from "react";
import {
  Edit,
  Trash2,
  Search,
  MessageCircle,
  Star,
  CheckCircle,
  XCircle,
} from "lucide-react";
import {
  getAllComments,
  updateComment,
  deleteComment,
} from "../lib/pocketbase";
import { Comment } from "../types";
import { formatPersianDate } from "../lib/utils";
import {
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableHeaderCell,
} from "../components/ui/Table";
import Input from "../components/ui/Input";
import Textarea from "../components/ui/Textarea";
import Button from "../components/ui/Button";

const CommentsPage: React.FC = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editForm, setEditForm] = useState({
    text: "",
    rate: 0,
  });

  const fetchComments = useCallback(async () => {
    try {
      const data = await getAllComments();
      setComments(data);
    } catch (error: any) {
      if (error?.message?.includes("autocancelled")) {
        return;
      }
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleEdit = (comment: Comment) => {
    setEditingComment(comment);
    setEditForm({
      text: comment.text,
      rate: comment.rate,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingComment) return;

    try {
      await updateComment(editingComment.id, {
        text: editForm.text,
        rate: editForm.rate,
      });
      setEditingComment(null);
      fetchComments();
    } catch (error) {
      console.error("Error updating comment:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditForm({ text: "", rate: 0 });
  };

  const handleToggleActive = async (comment: Comment) => {
    try {
      await updateComment(comment.id, {
        is_active: !comment.is_active,
      });
      fetchComments();
    } catch (error) {
      console.error("Error toggling comment status:", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("آیا از حذف این نظر اطمینان دارید؟")) {
      try {
        await deleteComment(id);
        fetchComments();
      } catch (error) {
        console.error("Error deleting comment:", error);
      }
    }
  };

  const filteredComments = comments.filter((comment) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      comment.text.toLowerCase().includes(searchLower) ||
      comment.expand?.user_id?.first_name
        ?.toLowerCase()
        .includes(searchLower) ||
      comment.expand?.user_id?.last_name
        ?.toLowerCase()
        .includes(searchLower) ||
      comment.expand?.user_id?.email.toLowerCase().includes(searchLower)
    );
  });

  if (loading && comments.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">مدیریت نظرات</h1>
          <p className="text-gray-600">مشاهده و مدیریت نظرات کاربران</p>
        </div>
      </div>

      {/* Search */}
      <div className="card p-6">
        <div className="flex items-center mb-4">
          <Search className="h-5 w-5 text-gray-500 ml-2" />
          <h3 className="text-lg font-medium text-gray-900">جستجو</h3>
        </div>
        <Input
          label="جستجو در نظرات"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="جستجو بر اساس متن نظر، نام کاربر یا ایمیل..."
        />
      </div>

      {/* Comments Table */}
      <div className="card">
        <Table>
          <TableHeader>
            <TableHeaderCell>کاربر</TableHeaderCell>
            <TableHeaderCell>متن نظر</TableHeaderCell>
            <TableHeaderCell>امتیاز</TableHeaderCell>
            <TableHeaderCell>وضعیت</TableHeaderCell>
            <TableHeaderCell>تاریخ ایجاد</TableHeaderCell>
            <TableHeaderCell>عملیات</TableHeaderCell>
          </TableHeader>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredComments.map((comment) => (
              <TableRow key={comment.id}>
                <TableCell>
                  {comment.expand?.user_id ? (
                    <div>
                      <div className="font-medium text-gray-900">
                        {comment.expand.user_id.first_name &&
                        comment.expand.user_id.last_name
                          ? `${comment.expand.user_id.first_name} ${comment.expand.user_id.last_name}`
                          : comment.expand.user_id.first_name ||
                            comment.expand.user_id.last_name ||
                            "بدون نام"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {comment.expand.user_id.email}
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-400">کاربر حذف شده</span>
                  )}
                </TableCell>
                <TableCell className="!whitespace-normal max-w-[200px] sm:max-w-xs">
                  {editingComment?.id === comment.id ? (
                    <Textarea
                      value={editForm.text}
                      onChange={(e) =>
                        setEditForm({ ...editForm, text: e.target.value })
                      }
                      className="w-full"
                    />
                  ) : (
                    <p className="text-gray-900 break-words">{comment.text}</p>
                  )}
                </TableCell>
                <TableCell>
                  {editingComment?.id === comment.id ? (
                    <Input
                      type="number"
                      value={editForm.rate}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          rate: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-24"
                      min={0}
                      max={5}
                    />
                  ) : (
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 ml-1" />
                      <span className="text-gray-900">{comment.rate}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => handleToggleActive(comment)}
                    className={`flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      comment.is_active
                        ? "bg-green-100 text-green-800 hover:bg-green-200"
                        : "bg-red-100 text-red-800 hover:bg-red-200"
                    }`}
                  >
                    {comment.is_active ? (
                      <>
                        <CheckCircle className="h-4 w-4 ml-1" />
                        فعال
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 ml-1" />
                        غیرفعال
                      </>
                    )}
                  </button>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500">
                    {formatPersianDate(comment.created)}
                  </span>
                </TableCell>
                <TableCell>
                  {editingComment?.id === comment.id ? (
                    <div className="flex items-center space-x-2 space-x-reverse">
                      <Button
                        onClick={handleSaveEdit}
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                      >
                        ذخیره
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        size="sm"
                        variant="outline"
                      >
                        انصراف
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 space-x-reverse">
                      <button
                        onClick={() => handleEdit(comment)}
                        className="p-1 text-primary-600 hover:text-primary-700 transition-colors"
                        title="ویرایش"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(comment.id)}
                        className="p-1 text-red-600 hover:text-red-700 transition-colors"
                        title="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Empty State */}
      {filteredComments.length === 0 && !loading && (
        <div className="text-center py-12">
          <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            هیچ نظری یافت نشد
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm
              ? "فیلترهای خود را تغییر دهید"
              : "هنوز نظری ثبت نشده است"}
          </p>
        </div>
      )}
    </div>
  );
};

export default CommentsPage;

